/**
 * DefinitionsNav — the docs-section table of contents (screen only, print:hidden).
 * A text filter over the catalogue plus pillar jump-links grouped by cadence
 * chapter, anchored to the `pillar-${cadence}-${pillar}` headings the catalogue
 * renders. Sticky on wide screens so it stays put while the definitions scroll.
 * Presentational: filter state is owned by the Definitions page (CD-7).
 */

import React from 'react'
import { Search } from 'lucide-react'
import type { Cadence, Pillar } from '../../types/metrics'

export interface NavChapter {
  cadence: Cadence
  title: string
  pillars: Pillar[]
}

interface DefinitionsNavProps {
  chapters: NavChapter[]
  query: string
  onQueryChange: (value: string) => void
}

function ChapterLinks({ chapter }: { chapter: NavChapter }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">
        {chapter.title}
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {chapter.pillars.map((pillar) => (
          <li key={pillar}>
            <a
              href={`#pillar-${chapter.cadence}-${pillar}`}
              className="block rounded px-2 py-1 text-xs text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              {pillar}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function DefinitionsNav({
  chapters,
  query,
  onQueryChange,
}: DefinitionsNavProps): React.JSX.Element {
  return (
    <nav
      aria-label="Definitions contents"
      className="mb-6 print:hidden lg:sticky lg:top-20 lg:mb-0 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto"
    >
      <label className="relative block">
        <span className="sr-only">Filter definitions</span>
        <Search
          size={14}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Filter definitions…"
          className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        />
      </label>
      <div className="mt-4 space-y-4">
        {chapters.map((chapter) => (
          <ChapterLinks key={chapter.title} chapter={chapter} />
        ))}
      </div>
    </nav>
  )
}
